<script setup>
import { ref, onMounted } from 'vue';
import { setMeta } from '@/utils/util';

onMounted(() => {
  document.title = "Image Compressor - Optimize Your Images Online";
  setMeta("description", "Free online image compression tool. Reduce image file size while maintaining quality. Support JPG, PNG and other formats. Perfect for website optimization and faster loading times.");
  setMeta("keywords", "image compressor,image optimizer,image compression,photo compression,picture optimizer,reduce image size,compress jpg,compress png");

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Image Compressor",
    "description": "Online image compression tool for optimizing image file sizes",
    "applicationCategory": "MultimediaApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(structuredData);
  document.head.appendChild(script);
});
import { ElMessage } from 'element-plus';
import imageCompression from 'browser-image-compression';
import JSZip from 'jszip';

const fileList = ref([]);
const loading = ref(false);
const quality = ref(0.8);
const maxWidthOrHeight = ref(1920);
const compressionResults = ref([]);
const compressedZipBlob = ref(null);

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

onMounted(() => {
  document.title = "Image Compressor - Online Image Compression Tool";
  setMeta("description", "Free online image compression tool. Reduce image file size while maintaining quality. Support JPG, PNG, and other formats.");
  setMeta("keywords", "image compressor,image compression,reduce image size,compress photos,online image tool");
});

const handleUpload = async (files) => {
  if (!files.length) {
    ElMessage.warning('Please select images first');
    return;
  }

  loading.value = true;
  const zip = new JSZip();
  const compressedFolder = zip.folder("compressed_images");
  compressionResults.value = [];
  compressedZipBlob.value = null;

  try {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: maxWidthOrHeight.value,
      useWebWorker: true,
      quality: quality.value,
    };

    // 压缩所有图片
    const compressPromises = files.map(async (file) => {
      try {
        const compressedBlob = await imageCompression(file.raw, options);
        const result = {
          name: file.name,
          originalSize: formatFileSize(file.raw.size),
          compressedSize: formatFileSize(compressedBlob.size),
          compressionRatio: ((1 - compressedBlob.size / file.raw.size) * 100).toFixed(1) + '%'
        };
        compressionResults.value.push(result);
        return {
          name: file.name,
          blob: compressedBlob
        };
      } catch (error) {
        console.error(`Error compressing ${file.name}:`, error);
        return null;
      }
    });

    const compressedFiles = await Promise.all(compressPromises);

    // 添加到zip
    compressedFiles.forEach(file => {
      if (file) {
        compressedFolder.file(file.name, file.blob);
      }
    });

    // 生成zip blob
    compressedZipBlob.value = await zip.generateAsync({ type: "blob" });
    ElMessage.success('Images compressed successfully!');
    fileList.value = [];
  } catch (error) {
    console.error('Compression failed:', error);
    ElMessage.error('Failed to compress images');
  } finally {
    loading.value = false;
  }
};

const downloadZip = () => {
  if (!compressedZipBlob.value) {
    ElMessage.warning('No compressed images available');
    return;
  }
  const link = document.createElement('a');
  link.href = URL.createObjectURL(compressedZipBlob.value);
  link.download = "compressed_images.zip";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const handleExceed = (files) => {
  ElMessage.warning(
    'You can select up to 10 files at once'
  );
};

const beforeUpload = (file) => {
  const isImage = file.type.startsWith('image/');
  if (!isImage) {
    ElMessage.error('You can only upload image files!');
  }
  return isImage;
};

const handleRemove = (file) => {
  const index = fileList.value.indexOf(file);
  if (index !== -1) {
    fileList.value.splice(index, 1);
  }
};
</script>

<template>
  <div class="tool-container">
    <h1 style="margin-bottom: 20px">Image Compressor</h1>
    
    <div class="compression-options">
      <div class="option-group">
        <label>Quality (0.1-1.0):</label>
        <el-slider
          v-model="quality"
          :min="0.1"
          :max="1"
          :step="0.1"
          :marks="{
            0.1: '0.1',
            0.5: '0.5',
            1: '1.0'
          }"
        />
      </div>
      
      <div class="option-group">
        <label>Max Width/Height (px):</label>
        <el-input-number
          v-model="maxWidthOrHeight"
          :min="100"
          :max="4000"
          :step="100"
        />
      </div>
    </div>

    <div class="upload-section">
      <el-upload
        class="upload-demo"
        drag
        multiple
        :auto-upload="false"
        :limit="10"
        :on-exceed="handleExceed"
        :before-upload="beforeUpload"
        :on-remove="handleRemove"
        v-model:file-list="fileList"
        accept="image/*"
      >
        <el-icon class="el-icon--upload"><upload-filled /></el-icon>
        <div class="el-upload__text">
          Drop files here or <em>click to upload</em>
        </div>
        <template #tip>
          <div class="el-upload__tip">
            Only image files are allowed (JPG/PNG/etc.)
          </div>
        </template>
      </el-upload>
      
      <div class="buttons">
        <el-button
          type="primary"
          @click="handleUpload(fileList)"
          :loading="loading"
          :disabled="fileList.length === 0"
        >
          Start Compress
        </el-button>
      </div>

      <!-- Add compression results table -->
      <div v-if="compressionResults.length > 0" class="compression-results">
        <div class="results-header">
          <h3>Compression Results</h3>
          <el-button
            type="primary"
            @click="downloadZip"
            :disabled="!compressedZipBlob"
          >
            Download ZIP
          </el-button>
        </div>
        <el-table :data="compressionResults" stripe>
          <el-table-column prop="name" label="File Name" />
          <el-table-column prop="originalSize" label="Original Size" width="120" />
          <el-table-column prop="compressedSize" label="Compressed Size" width="120" />
          <el-table-column prop="compressionRatio" label="Reduction" width="100" />
        </el-table>
      </div>
    </div>

    <div class="introduce">
      <h2>What is Image Compressor?📸</h2>
      <p>
        Image Compressor is a free online tool that helps you reduce the file size of your images
        while maintaining good visual quality. Perfect for optimizing images for websites,
        reducing storage space, or sharing via email.
      </p>
      
      <h3>Features</h3>
      <ul>
        <li>Batch compression support - compress multiple images at once</li>
        <li>Support for multiple image formats (JPG, PNG, etc.)</li>
        <li>Adjustable compression quality</li>
        <li>Control maximum image dimensions</li>
        <li>Automatic ZIP packaging for batch downloads</li>
        <li>Free to use and no registration required</li>
      </ul>

      <h3>Usage Tips</h3>
      <ul>
        <li>For web images, a quality setting of 0.7-0.8 usually provides good results</li>
        <li>The max width/height setting helps ensure your images aren't too large</li>
        <li>You can compress up to 10 images at once</li>
        <li>All compressed images will be packaged into a ZIP file for easy download</li>
        <li>Drag and drop multiple files to upload them quickly</li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tool-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.compression-options {
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  .option-group {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }

    label {
      display: block;
      margin-bottom: 10px;
      font-weight: bold;
      color: #606266;
    }
  }
}

.upload-section {
  margin-bottom: 30px;
  
  .buttons {
    margin-top: 20px;
    display: flex;
    gap: 10px;
    justify-content: center;
  }
}

.introduce {
  margin-top: 40px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  h2 {
    margin-bottom: 16px;
  }

  h3 {
    margin: 20px 0 10px;
    color: #2c3e50;
  }

  p {
    margin-bottom: 16px;
    line-height: 1.6;
    color: #606266;
  }

  ul {
    margin: 0;
    padding-left: 20px;
    
    li {
      margin-bottom: 8px;
      color: #606266;
    }
  }
}

.compression-results {
  margin-top: 30px;
  
  .results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;

    h3 {
      margin: 0;
      color: #2c3e50;
    }
  }
}

:deep(.el-table) {
  margin-top: 10px;
  border-radius: 8px;
  overflow: hidden;
  
  .el-table__header-wrapper {
    background-color: #f8f9fa;
  }
}

:deep(.el-upload-dragger) {
  width: 100%;
  height: 200px;
}

:deep(.el-upload) {
  width: 100%;
}

:deep(.el-upload-list) {
  width: 100%;
}
</style>